import { z } from 'zod';
import { peopleFilterSchema } from './people-filter.js';
import { projectRoleSchema } from './domain.js';
/*
  A MEMBERSHIP IS A GRANT, not a role.

  Four roles were the entire authorisation model — `project_members.role` is the only permission this
  product has ever persisted. That is right for "what may they do" and answers nothing about "what may
  they see", and on a separation engagement the second question is the one a client asks: a country
  lead needs their own population and has no business with the rest of the organisation's names and
  salaries.

  TWO AXES, DELIBERATELY SEPARATE. `scope` narrows WHICH PEOPLE; `salaryHidden` narrows WHAT ABOUT
  them. They are independent because the real requests are independent — "Germany only" and "no
  compensation" are asked by different people for different reasons, and collapsing them into one
  setting would mean granting one to get the other.
*/
/**
 * The people a member may see, written in the vocabulary the assistant already uses.
 *
 * `peopleFilterSchema` MINUS THREE FIELDS, and each removal is load-bearing rather than tidy.
 *
 * `includeInactive` and `includeOpenPositions` are gone because they have DEFAULTS — `false` and
 * `true` — chosen for a question about aggregates, and a scope is not that question. Leavers and
 * inactive rows are in the roster and out of every total, so removing them from a scoped member's
 * payload would change the denominator of every count on their screen. It would look like a scope
 * decision and actually be a schema default nobody chose. `inScope` in `@integrate/core` supplies
 * both as `true`; see its comment for the whole argument.
 *
 * `bucket` is gone for a harder reason: it is PER-SCENARIO DECISION STATE. A permission keyed on it
 * would move when somebody made a decision — a person visible in Scenario A and not in Scenario B,
 * changing under a reader mid-session, and access re-decided as a side effect of a colleague
 * designing. A permission has to be a function of the roster only.
 *
 * AT LEAST ONE FIELD IS REQUIRED. An empty scope object matches everybody and is therefore
 * indistinguishable from `null` — one meaning representable two ways is how a boundary ends up
 * accidentally disabled by a form that submitted its defaults.
 */
export const memberScopeSchema = peopleFilterSchema
    .omit({ bucket: true, includeInactive: true, includeOpenPositions: true })
    .refine((s) => Object.values(s).some((v) => v !== undefined), {
    message: 'A scope has to name something. Leave it unset for access to the whole organisation.',
});
/**
 * What a membership actually is: a role, a population, and whether compensation travels with it.
 *
 * `scope: null` is the whole organisation, which is what every membership carries until one is
 * granted — so this is a widening of the existing model rather than a replacement, and an unscoped
 * member's payload is byte-identical to what it was.
 */
export const grantSchema = z.object({
    role: projectRoleSchema,
    scope: memberScopeSchema.nullable(),
    salaryHidden: z.boolean(),
});
/** A grant that narrows nothing — the shape every existing membership has. */
export function unrestricted(role) {
    return { role, scope: null, salaryHidden: false };
}
/**
 * Whether this grant narrows anything at all.
 *
 * The cheap early-out that keeps the unscoped path free: `applyScope` returns the state it was given
 * by REFERENCE when this is false, so a payload nobody is narrowing is not rebuilt and cannot have
 * changed shape. That property is what `tests/parity`'s 603,176 values depend on.
 */
export function isRestricted(grant) {
    return grant.scope !== null || grant.salaryHidden;
}
/** Whether the population itself is narrowed, as against only the salary field being withheld. */
export function isScoped(grant) {
    return grant.scope !== null;
}
//# sourceMappingURL=scope.js.map